import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/api";

const JSON_FIELDS = [
  "images",
  "galleryExterior",
  "galleryInterior",
  "deckPlans",
  "engineOptions",
];

function parseMaybeJSON(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeYacht(yacht) {
  if (!yacht || typeof yacht !== "object") return yacht;
  const out = { ...yacht };
  for (const field of JSON_FIELDS) {
    if (field in out) out[field] = parseMaybeJSON(out[field]);
  }
  return out;
}

export const fetchYachts = createAsyncThunk(
  "yachts/fetchYachts",
  async (params = {}) => {
    const { data } = await api.get("/yachts", { params });
    return { ...data, yachts: (data.yachts || []).map(normalizeYacht) };
  },
);

export const fetchFeaturedYachts = createAsyncThunk(
  "yachts/fetchFeaturedYachts",
  async () => {
    const { data } = await api.get("/yachts/featured");
    return { ...data, yachts: (data.yachts || []).map(normalizeYacht) };
  },
);

export const fetchYachtById = createAsyncThunk(
  "yachts/fetchYachtById",
  async (id) => {
    const { data } = await api.get(`/yachts/${id}`);
    return normalizeYacht(data);
  },
);

const yachtsSlice = createSlice({
  name: "yachts",
  initialState: {
    items: [],
    count: 0,
    featured: [],
    current: null,
    loading: false,
    featuredLoading: false,
    currentLoading: false,
    error: null,
  },
  reducers: {
    clearCurrentYacht: (state) => {
      state.current = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchYachts
      .addCase(fetchYachts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchYachts.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.yachts;
        state.count = action.payload.count;
      })
      .addCase(fetchYachts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // fetchFeaturedYachts
      .addCase(fetchFeaturedYachts.pending, (state) => {
        state.featuredLoading = true;
      })
      .addCase(fetchFeaturedYachts.fulfilled, (state, action) => {
        state.featuredLoading = false;
        state.featured = action.payload.yachts;
      })
      .addCase(fetchFeaturedYachts.rejected, (state, action) => {
        state.featuredLoading = false;
        state.error = action.error.message;
      })
      // fetchYachtById
      .addCase(fetchYachtById.pending, (state) => {
        state.currentLoading = true;
        state.error = null;
      })
      .addCase(fetchYachtById.fulfilled, (state, action) => {
        state.currentLoading = false;
        state.current = action.payload;
      })
      .addCase(fetchYachtById.rejected, (state, action) => {
        state.currentLoading = false;
        state.error = action.error.message;
      });
  },
});

export const { clearCurrentYacht } = yachtsSlice.actions;
export default yachtsSlice.reducer;
